import { openDB } from 'idb';
import {
  getLocalStorage,
  safeGetJsonStorageItem,
  safeSetStorageItem,
} from '../browserStorage';
import { DATA_VERSION } from './version';

export class DataManager {
  constructor(accountId) {
    this.modelsToSync = ['inbox', 'label'];
    this.accountId = accountId;
    this.db = null;
    this.dbPromise = null;
  }

  initDb() {
    if (this.db) return Promise.resolve(this.db);
    if (this.dbPromise) return this.dbPromise;

    const dbName = `otello-store-${this.accountId}`;
    let openedDb = null;
    let connectionReleased = false;

    const releaseConnection = (event = null) => {
      if (connectionReleased) return;
      connectionReleased = true;

      const eventDatabase = event?.target;
      eventDatabase?.close?.();
      if (openedDb && openedDb !== eventDatabase) {
        openedDb.close?.();
      }

      // A stale connection may receive versionchange after a newer one has
      // already opened. Only invalidate the connection owned by this attempt.
      if (this.db === openedDb) {
        this.db = null;
      }
    };

    const openingPromise = Promise.resolve()
      .then(() =>
        openDB(dbName, DATA_VERSION, {
          blocking(_currentVersion, _blockedVersion, event) {
            // Logout/version cleanup deletes this shared database. Release the
            // connection so another tab is not left waiting on openDB forever.
            releaseConnection(event);
          },
          terminated() {
            releaseConnection();
          },
          upgrade(db) {
            db.createObjectStore('cache-keys');
            db.createObjectStore('inbox', { keyPath: 'id' });
            db.createObjectStore('label', { keyPath: 'id' });
          },
        })
      )
      .then((db) => {
        openedDb = db;
        this.db = db;

        const storage = getLocalStorage();
        const dbNames = safeGetJsonStorageItem(storage, 'otello-idb-names', []);
        const trackedDbNames = Array.isArray(dbNames) ? dbNames : [];

        if (!trackedDbNames.includes(dbName)) {
          trackedDbNames.push(dbName);
          safeSetStorageItem(
            storage,
            'otello-idb-names',
            JSON.stringify(trackedDbNames)
          );
        }

        return db;
      });

    const trackedPromise = openingPromise.finally(() => {
      if (this.dbPromise === trackedPromise) {
        this.dbPromise = null;
      }
    });

    this.dbPromise = trackedPromise;
    return trackedPromise;
  }

  validateModel(name) {
    if (!name) throw new Error('Model name is not defined');
    if (!this.modelsToSync.includes(name)) {
      throw new Error(`Model ${name} is not defined`);
    }
    return true;
  }

  async replace({ modelName, data }) {
    this.validateModel(modelName);

    // Clear and repopulate in one transaction so readers can only observe the
    // complete old snapshot or the complete new snapshot, never an empty gap.
    const tx = this.db.transaction(modelName, 'readwrite');
    const requests = [tx.store.clear()];

    if (Array.isArray(data)) {
      data.forEach((item) => requests.push(tx.store.put(item)));
    } else {
      requests.push(tx.store.put(data));
    }

    await Promise.all([...requests, tx.done]);
  }

  async push({ modelName, data }) {
    this.validateModel(modelName);

    if (Array.isArray(data)) {
      const tx = this.db.transaction(modelName, 'readwrite');
      data.forEach((item) => {
        tx.store.put(item);
      });
      await tx.done;
    } else {
      await this.db.put(modelName, data);
    }
  }

  async get({ modelName }) {
    this.validateModel(modelName);
    return this.db.getAll(modelName);
  }

  async setCacheKeys(cacheKeys) {
    await Promise.all(
      Object.keys(cacheKeys).map((modelName) =>
        this.db.put('cache-keys', cacheKeys[modelName], modelName)
      )
    );
  }

  async getCacheKey(modelName) {
    this.validateModel(modelName);

    return this.db.get('cache-keys', modelName);
  }
}
